import { useCallback, useEffect, useState } from 'react';

/**
 * Reserve the top chrome's height as felt space, instead of painting over it.
 *
 * The "Your Tables" strip, the Add Table button and the ticker live in an
 * overlay mounted as a SIBLING above the felt row (`Game.tsx` → `FixedChrome`),
 * at z-index 50/60. The felt row declares no z-index of its own and is
 * `overflow: hidden`, so it can neither out-rank the chrome nor grow to dodge
 * it. Seat positions are percentages of the whole container, so the top seats
 * are placed underneath whatever chrome happens to be there — and their cards
 * with them.
 *
 * Nothing about that is fixable with z-index: raising the felt would put seats
 * over the navigation, which is worse. The only real fix is for the felt to
 * stop claiming space the chrome has already taken, which is what this does —
 * it publishes the chrome's measured height and `gamePageLayout`'s `tableRow`
 * pads by it. The ring then re-lays itself out inside the smaller box for free,
 * because it was always expressed in percentages of that box.
 *
 * WHY A CSS VARIABLE AND NOT STATE. The publisher (the header) and the consumer
 * (the felt row) are siblings with no shared owner below the page, so threading
 * this through React would mean lifting state into `Game.tsx` and re-rendering
 * the whole page on every chrome resize. A custom property crosses the same gap
 * without a render.
 *
 * RESIZE-LOOP SAFETY. The observed element is the CHROME, which lives OUTSIDE
 * the felt subtree, and the property is consumed only by the felt. So the felt
 * resizing can never feed this observer — the cycle the loop would need does not
 * exist. (Observing something inside the felt and then resizing the felt is
 * exactly the arrangement `useShrinkToFit`'s invariant note warns about.)
 *
 * The height is rounded to whole pixels before it is written: sub-pixel jitter
 * in a flex/scaled header would otherwise write a new value every frame, and
 * each write re-lays out the felt.
 */
export const CHROME_INSET_VAR = '--table-chrome-inset';

/**
 * Returns a CALLBACK ref to put on the chrome element to be reserved.
 *
 * A callback ref rather than a `useRef` object on purpose: the strip renders
 * inside a conditional branch (no open tables, guest, stacked vs spread), so the
 * element mounts and unmounts long after the hook first runs. An effect keyed on
 * a ref OBJECT never re-runs when that happens — it would measure whatever was
 * there on mount and then go quiet, which is worse than not measuring at all
 * because the felt would keep a reservation for chrome that is no longer there.
 */
/**
 * Every element currently claiming the reservation, in mount order.
 *
 * MODULE level because the property is global and there can legitimately be more
 * than one publisher at a time. `FixedChrome` keeps the OUTGOING `<TableHeader>`
 * mounted for a lame-duck frame after the incoming one activates, so every table
 * swipe briefly has two. With an unconditional `removeProperty` in cleanup, the
 * lame duck deleted the live header's reservation on its way out — and nothing
 * ever restored it, because the survivor's observer only fires when ITS OWN
 * strip resizes, which it has no reason to do. `tableRow` fell back to `0px` and
 * the top seats went back under the strip permanently: the fix silently
 * reverting itself, one swipe in.
 *
 * A set of claimants makes the last one out turn off the light, and only then.
 */
const claimants = new Set<HTMLElement>();

const publishTallestClaim = (): void => {
  const root = document.documentElement;
  if (claimants.size === 0) {
    root.style.removeProperty(CHROME_INSET_VAR);
    return;
  }
  // Reserve for the TALLEST claimant. During a swipe the two headers are the
  // same height, so this is just "the one that is there"; if they ever differ,
  // under-reserving is what puts cards behind chrome, so round up.
  let tallest = 0;
  claimants.forEach((element) => {
    tallest = Math.max(tallest, Math.ceil(element.getBoundingClientRect().height));
  });
  root.style.setProperty(CHROME_INSET_VAR, `${tallest}px`);
};

export const useChromeInset = (): ((node: HTMLElement | null) => void) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const attach = useCallback((node: HTMLElement | null) => setElement(node), []);

  useEffect(() => {
    // Not rendered (guest, no open tables, or a layout with no strip): claim
    // nothing, and let any other publisher keep its reservation.
    if (!element) {
      publishTallestClaim();
      return;
    }

    claimants.add(element);
    publishTallestClaim();

    // `ResizeObserver` may be absent in older test environments; the initial
    // measurement above still applies, so the felt is never left un-inset.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishTallestClaim) : undefined;
    observer?.observe(element);

    return () => {
      observer?.disconnect();
      claimants.delete(element);
      // Re-publish rather than remove: if another header is still up, its
      // reservation stands.
      publishTallestClaim();
    };
  }, [element]);

  return attach;
};
